import { compact, isNil, mapValues, some } from 'lodash';
import { useMemo } from 'react';

import { renameSensorDatumKeys } from '@/api/historicalData/helpers';
import { CurrentsDatum, PerSensor } from '@/api/historicalData/types';
import { UseHistoricalSensorDataOptions, useHistoricalSensorData } from '@/api/historicalData/useHistoricalSensorData';
import { convert } from '@/common/convert';
import { usePreferredUnits } from '@/hooks/usePreferredUnits';

export const CURRENTS_SPEED_DATA_KEY = 'aanderaa_abs_speed_mean_15bits';
export const CURRENTS_STDDEV_DATA_KEY = 'aanderaa_abs_speed_std_15bits';
export const CURRENTS_DIRECTION_DATA_KEY = 'aanderaa_direction_circ_mean_13bits';
export const CURRENTS_TILT_DATA_KEY = 'aanderaa_abs_tilt_mean_8bits';

const CURRENTS_DATA_KEYS = [
    CURRENTS_SPEED_DATA_KEY,
    CURRENTS_STDDEV_DATA_KEY,
    CURRENTS_DIRECTION_DATA_KEY,
    CURRENTS_TILT_DATA_KEY,
];

export const useCurrentsData = (options: UseHistoricalSensorDataOptions) => {
    const { data: sensorData, ...response } = useHistoricalSensorData(options);

    const targetUnit = usePreferredUnits('currentSpeed');

    const data: PerSensor<CurrentsDatum[]> | undefined = useMemo(() => {
        if (!sensorData) return undefined;

        return mapValues(sensorData, (d) =>
            compact(
                d.map((datum) => {
                    if (!some(CURRENTS_DATA_KEYS, (key) => key in datum.data)) {
                        return null;
                    }

                    return {
                        timestamp: datum.timestamp,
                        ...renameSensorDatumKeys(datum, CURRENTS_SPEED_DATA_KEY, 'speed', targetUnit),
                        speedUnitType: 'currentSpeed',
                        speedStdDev: isNil(datum.data[CURRENTS_STDDEV_DATA_KEY])
                            ? undefined
                            : convert(datum.data[CURRENTS_STDDEV_DATA_KEY].value)
                                  .from(datum.data[CURRENTS_STDDEV_DATA_KEY].units)
                                  .to(targetUnit),
                        ...renameSensorDatumKeys(datum, CURRENTS_DIRECTION_DATA_KEY, 'direction', 'deg'),
                        ...renameSensorDatumKeys(datum, CURRENTS_TILT_DATA_KEY, 'sensorTilt', 'deg'),
                    } as CurrentsDatum;
                })
            )
        );
    }, [targetUnit, sensorData]);

    return { data, ...response };
};
